import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import {
  iosNativeCenterHeaderLeft,
  iosNativeContent,
  iosNativePageRoot,
  iosNativeScreenTitle,
} from '../styles/iosNativePageCss';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { useAWSAuth as useAuth } from '../contexts/AWSAuthContext';
import { listUserScans, type ScanRecord } from '../services/scanService';
import { startOfDay, differenceInDays } from 'date-fns';
import { formatStreakLabel, calculateStreakFromScans } from '../utils/streakCalculator';
import { useSubscription } from '../hooks/useSubscription';
import { 
  User, 
  Mail, 
  Calendar, 
  TrendingUp, 
  Target, 
  Settings, 
  LogOut, 
  Edit3, 
  Save, 
  X,
  Crown,
  Star,
  Zap,
  Utensils
} from 'lucide-react';
import { useToast } from '../components/ui/Toast';

const PageContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: linear-gradient(180deg, ${theme.colors.background.secondary} 0%, ${theme.colors.background.primary} 100%);
  padding: 1.5rem 1rem 3rem;

  @media (min-width: ${theme.breakpoints.sm}) {
    padding: 2.5rem 2rem 3.5rem;
  }

  @media (min-width: ${theme.breakpoints.lg}) {
    padding: 3rem 3.5rem 4rem;
  }

  ${iosNativePageRoot}
`;

const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
  animation: slideUp 0.5s ease-out;
  display: grid;
  gap: 3rem;

  ${iosNativeContent}
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  ${iosNativeCenterHeaderLeft}
`;

const Title = styled.h1`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;

  ${iosNativeScreenTitle}
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.text.secondary};
`;

const ProfileGrid = styled.div`
  display: grid;
  gap: 2rem;
  grid-template-columns: minmax(0, 1fr);
  align-items: stretch;

  @media (min-width: ${theme.breakpoints.md}) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 2.5rem;
  }
`;

const ProfileCard = styled(Card)`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;


const ProfileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
  flex-wrap: wrap;
`;

const ProfileHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  min-width: 0;
`;

const Avatar = styled.div`
  width: 84px;
  height: 84px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${theme.colors.primary[400]}, ${theme.colors.mint[400]});
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  box-shadow: ${theme.shadows.lg};
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
`;

const NameRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
`;

const UserName = styled.h2`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const UserEmail = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  margin: 0;
  word-break: break-word;
`;

const SubscriptionBadge = styled(Badge)`
  font-size: ${theme.typography.fontSize.xs};
`;

const EditButton = styled(Button)`
  align-self: flex-start;
  white-space: nowrap;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
`;

const StatCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 140px;
  padding: 1.25rem;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius['2xl']};
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  text-align: center;

  &:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: ${theme.shadows.lg};
  }
`;

const StatValue = styled.div`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary[600]};
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
`;

const SectionDivider = styled.div`
  width: 100%;
  height: 1px;
  background: ${theme.colors.border.light};
  margin: 0.75rem 0;
`;

const SectionGroup = styled.div`
  display: grid;
  gap: 1.25rem;
`;

const SectionSubtitle = styled.h4`
  margin: 0;
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
`;

const TitleRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  color: ${theme.colors.text.primary};

  svg {
    color: ${theme.colors.primary[500]};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.primary};
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.base};
  background: white;
  color: ${theme.colors.text.primary};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[400]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[100]};
  }
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 2px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.base};
  background: white;
  color: ${theme.colors.text.primary};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[400]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[100]};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const InfoCard = styled(Card)`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  background: rgba(255, 255, 255, 0.9);

  svg {
    color: ${theme.colors.primary[500]};
    flex-shrink: 0;
  }
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  margin-bottom: 0.25rem;
`;

const InfoValue = styled.div`
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.primary};
`;

const GlowScoreMeter = styled.div`
  background: linear-gradient(90deg, ${theme.colors.mint[200]} 0%, ${theme.colors.primary[200]} 100%);
  height: 12px;
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
  margin: 0.5rem 0;
`;

const GlowScoreFill = styled.div<{ $score: number }>`
  height: 100%;
  background: linear-gradient(90deg, ${theme.colors.mint[400]} 0%, ${theme.colors.primary[500]} 100%);
  width: ${props => props.$score}%;
  transition: width 0.5s ease;
`;

const CancelButton = styled(Button)`
  background: transparent;
  border: 1.5px solid ${theme.colors.status.error};
  color: ${theme.colors.status.error};
  
  &:hover {
    background: ${theme.colors.status.error}10;
    border-color: ${theme.colors.status.error};
  }
`;

const ProfilePage: React.FC = () => {
  const { user, userProfile, updateUserProfile, signOut, getCurrentSession, cancelSubscription } = useAuth();
  const { tier } = useSubscription();
  const { showToast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [editedProfile, setEditedProfile] = useState<{
    name: string;
    skinType?: 'oily' | 'dry' | 'combination' | 'sensitive' | 'normal' | '';
    acneType?: 'mild' | 'moderate' | 'severe' | 'hormonal' | 'inflammatory' | 'post-acne' | '';
  }>({
    name: userProfile?.name || '',
    skinType: userProfile?.skinType || '',
    acneType: userProfile?.acneType || '',
  });
  const [scans, setScans] = useState<ScanRecord[]>([]);
  const [loadingScans, setLoadingScans] = useState(true);
  const navigate = useNavigate();

  // Fetch scans to calculate real-time stats
  useEffect(() => {
    const fetchScans = async () => {
      try {
        const session = await getCurrentSession();
        if (!session) {
          setLoadingScans(false);
          return;
        }

        const uid = session.getIdToken().payload.sub;
        const response = await listUserScans(session, uid, 100); // Get up to 100 scans
        
        const completedScans = response.items.filter(scan => scan.status === 'complete');
        setScans(completedScans);
      } catch (err: any) {
        console.error('Failed to fetch scans for profile', err);
      } finally {
        setLoadingScans(false);
      }
    };

    if (userProfile) {
      fetchScans();
    }
  }, [getCurrentSession, userProfile]);

  // Calculate real-time streak from actual scan data
  const realTimeStats = useMemo(() => {
    const completedScans = scans.filter(s => s.status === 'complete');
    const totalScans = completedScans.length;

    // Day streak - calculate from actual scan data (Snapchat-style)
    // This ensures accuracy by looking at all scans, not just stored value
    const streakData = calculateStreakFromScans(completedScans);
    const streakCount = streakData.streakCount;
    const streakLabel = formatStreakLabel(streakCount);

    // Get latest scan's glow score
    const latestScan = completedScans.sort(
      (a, b) => new Date(b.createdAt ?? b.timestamp).getTime() - new Date(a.createdAt ?? a.timestamp).getTime()
    )[0];
    const currentGlowScore = latestScan 
      ? (latestScan.analysis?.skinHealthIndex ?? latestScan.glowScore ?? 0)
      : (userProfile?.glowScore ?? 0);

    // Extract skin type and acne type from latest scan analysis
    let detectedSkinType: 'oily' | 'dry' | 'combination' | 'sensitive' | 'normal' | undefined = undefined;
    let detectedAcneType: 'mild' | 'moderate' | 'severe' | 'hormonal' | 'inflammatory' | 'post-acne' | undefined = undefined;

    if (latestScan?.analysis) {
      // Map acne severity to acne type (support both old and new severity values)
      // Check both legacy acneSeverity and new acneAnalysis.overallSeverity
      const acneSeverity = latestScan.analysis.acneAnalysis?.overallSeverity || latestScan.analysis.acneSeverity;
      
      if (acneSeverity) {
        if (acneSeverity === 'mild' || acneSeverity === 'very_mild' || acneSeverity === 'none') {
          detectedAcneType = 'mild';
        } else if (acneSeverity === 'moderate' || acneSeverity === 'mild_to_moderate') {
          detectedAcneType = 'moderate';
        } else if (acneSeverity === 'moderate_to_severe' || acneSeverity === 'severe') {
          detectedAcneType = 'severe';
        }
        // Legacy support for old string values
        else if (acneSeverity === 'Mild' as any) detectedAcneType = 'mild';
        else if (acneSeverity === 'Moderate' as any) detectedAcneType = 'moderate';
        else if (acneSeverity === 'Elevated' as any || acneSeverity === 'Severe' as any) detectedAcneType = 'severe';
      }

      // Estimate skin type from pH and texture notes
      // Check both legacy estimatedPH and new acneAnalysis.estimatedPH
      const estimatedPH = latestScan.analysis.acneAnalysis?.estimatedPH ?? latestScan.analysis.estimatedPH;
      const textureNotes = (latestScan.analysis.acneAnalysis?.textureSummary || latestScan.analysis.textureNotes || '').toLowerCase();
      
      if (typeof estimatedPH === 'number') {
        if (estimatedPH < 4.7 || textureNotes.includes('sensitive')) {
          detectedSkinType = 'sensitive';
        } else if (estimatedPH > 5.9 || textureNotes.includes('dry')) {
          detectedSkinType = 'dry';
        } else if (textureNotes.includes('oily') || textureNotes.includes('t-zone')) {
          detectedSkinType = 'combination';
        } else if (estimatedPH >= 4.7 && estimatedPH <= 5.9) {
          detectedSkinType = 'normal';
        }
      } else {
        // Fallback to texture notes only if pH is not available
        if (textureNotes.includes('sensitive')) {
          detectedSkinType = 'sensitive';
        } else if (textureNotes.includes('dry')) {
          detectedSkinType = 'dry';
        } else if (textureNotes.includes('oily') || textureNotes.includes('t-zone')) {
          detectedSkinType = 'combination';
        }
      }
    }

    return {
      streak: streakCount,
      streakLabel,
      totalScans,
      glowScore: Math.round(currentGlowScore),
      detectedSkinType,
      detectedAcneType,
    };
  }, [scans, userProfile?.glowScore]);

  // Auto-update profile with detected skin/acne type from latest scan
  // Always update from the latest scan, regardless of existing values
  useEffect(() => {
    const updateProfileWithScanData = async () => {
      if (!userProfile || loadingScans || scans.length === 0) return;

      const updates: any = {};
      let needsUpdate = false;

      // Always update skin type from latest scan if detected
      if (realTimeStats.detectedSkinType) {
        // Only update if it's different from current value
        if (userProfile.skinType !== realTimeStats.detectedSkinType) {
          updates.skinType = realTimeStats.detectedSkinType;
          needsUpdate = true;
        }
      }

      // Always update acne type from latest scan if detected
      if (realTimeStats.detectedAcneType) {
        // Only update if it's different from current value
        if (userProfile.acneType !== realTimeStats.detectedAcneType) {
          updates.acneType = realTimeStats.detectedAcneType;
          needsUpdate = true;
        }
      }

      // Update glow score if it changed
      if (realTimeStats.glowScore !== userProfile.glowScore) {
        updates.glowScore = realTimeStats.glowScore;
        needsUpdate = true;
      }

      // Update total scans if it changed
      if (realTimeStats.totalScans !== userProfile.totalScans) {
        updates.totalScans = realTimeStats.totalScans;
        needsUpdate = true;
      }

      if (needsUpdate) {
        try {
          await updateUserProfile(updates);
        } catch (err) {
          console.warn('Failed to auto-update profile with scan data:', err);
        }
      }
    };

    updateProfileWithScanData();
  }, [realTimeStats, userProfile, loadingScans, scans.length, updateUserProfile]);

  const handleEdit = () => {
    setIsEditing(true);
    setEditedProfile({
      name: userProfile?.name || '',
      skinType: userProfile?.skinType || '',
      acneType: userProfile?.acneType || '',
    });
  };

  const handleSave = async () => {
    try {
      const updates: any = { ...editedProfile };
      // Convert empty strings to undefined
      if (updates.skinType === '') updates.skinType = undefined;
      if (updates.acneType === '') updates.acneType = undefined;
      await updateUserProfile(updates);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedProfile({
      name: userProfile?.name || '',
      skinType: userProfile?.skinType || '',
      acneType: userProfile?.acneType || '',
    });
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleCancelSubscription = async () => {
    // If user is not signed in/signed up, sign out and redirect to sign in page
    if (!user || !userProfile) {
      try {
        await signOut();
        navigate('/signin');
      } catch (error) {
        console.error('Error signing out:', error);
        navigate('/signin');
      }
      return;
    }

    // Get tier display name for confirmation message
    const tierDisplayName = tier === 'plus' ? 'GlowLens Plus' : tier === 'pro' ? 'GlowLens Pro' : 'GlowLens Elite';

    // Confirm cancellation
    const confirmed = window.confirm(
      `Are you sure you want to cancel your ${tierDisplayName} subscription? You will be downgraded to the Plus plan and lose access to premium features.`
    );

    if (!confirmed) return;

    setIsCancelling(true);
    try {
      await cancelSubscription();
      // Sign out the user after canceling subscription
      await signOut();
      showToast('Your subscription has been cancelled successfully. You have been signed out.', 'success');
      // Navigate to sign in page
      navigate('/signin');
    } catch (error) {
      console.error('Error canceling subscription:', error);
      showToast('Failed to cancel subscription. Please try again.', 'error');
      setIsCancelling(false);
    }
  };

  const getSubscriptionIcon = (tier: string) => {
    switch (tier) {
      case 'pro': return <Crown size={20} />;
      case 'plus': return <Star size={20} />;
      case 'student': return <Zap size={20} />;
      default: return <User size={20} />;
    }
  };

  const getSubscriptionColor = (tier: string) => {
    switch (tier) {
      case 'pro': return 'primary';
      case 'plus': return 'success';
      case 'student': return 'warning';
      default: return 'info';
    }
  };

  if (!userProfile) {
    return (
      <PageContainer>
        <Container>
          <div style={{ textAlign: 'center', padding: '4rem 0' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
            <h2>Loading your profile...</h2>
          </div>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container>
        <Header>
          <Title>My Profile</Title>
          <Subtitle>Manage your glow journey settings ✨</Subtitle>
        </Header>

        <ProfileGrid>
          <ProfileCard padding="lg">
            <ProfileHeader>
              <ProfileHeaderContent>
                <Avatar>
                  {userProfile.name.charAt(0).toUpperCase()}
                </Avatar>
                <ProfileInfo>
                  <NameRow>
                    <UserName>{userProfile.name}</UserName>
                    <SubscriptionBadge variant={getSubscriptionColor(userProfile.subscriptionTier) as any}>
                      {getSubscriptionIcon(userProfile.subscriptionTier)}
                      {userProfile.subscriptionTier.toUpperCase()}
                    </SubscriptionBadge>
                  </NameRow>
                  <UserEmail>{userProfile.email}</UserEmail>
                </ProfileInfo>
              </ProfileHeaderContent>

              {!isEditing && (
                <EditButton variant="outline" size="sm" onClick={handleEdit}>
                  <Edit3 size={16} />
                  Edit Profile
                </EditButton>
              )}
            </ProfileHeader>

            <StatsGrid>
              <StatCard>
                <StatValue>{realTimeStats.glowScore}</StatValue>
                <StatLabel>Glow Score</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{realTimeStats.streakLabel || realTimeStats.streak}</StatValue>
                <StatLabel>Streak 🔥</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{realTimeStats.totalScans}</StatValue>
                <StatLabel>Total Scans</StatLabel>
              </StatCard>
            </StatsGrid>

            <GlowScoreMeter>
              <GlowScoreFill $score={realTimeStats.glowScore} />
            </GlowScoreMeter>
            <div style={{ textAlign: 'center', fontSize: '0.875rem', color: theme.colors.text.secondary }}>
              {realTimeStats.glowScore}/100 Glow Score
            </div>
          </ProfileCard>

          <ProfileCard padding="lg">
            <SectionTitle>
              <TitleRow>
                <Settings size={20} />
                Profile Settings
              </TitleRow>
            </SectionTitle>

            {isEditing ? (
              <Form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                <SectionGroup>
                  <SectionSubtitle>Personal Info</SectionSubtitle>
                  <FormGroup>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      type="text"
                      value={editedProfile.name}
                      onChange={(e) => setEditedProfile({ ...editedProfile, name: e.target.value })}
                      required
                    />
                  </FormGroup>
                </SectionGroup>

                <SectionDivider />

                <SectionGroup>
                  <SectionSubtitle>Skin Info</SectionSubtitle>
                  <FormGroup>
                    <Label htmlFor="skinType">Skin Type</Label>
                    <Select
                      id="skinType"
                      value={editedProfile.skinType}
                      onChange={(e) => setEditedProfile({ ...editedProfile, skinType: e.target.value as any })}
                    >
                      <option value="">Select skin type</option>
                      <option value="oily">Oily</option>
                      <option value="dry">Dry</option>
                      <option value="combination">Combination</option>
                      <option value="sensitive">Sensitive</option>
                      <option value="normal">Normal</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="acneType">Acne Type</Label>
                    <Select
                      id="acneType"
                      value={editedProfile.acneType}
                      onChange={(e) => setEditedProfile({ ...editedProfile, acneType: e.target.value as any })}
                    >
                      <option value="">Select acne type</option>
                      <option value="mild">Mild</option>
                      <option value="moderate">Moderate</option>
                      <option value="severe">Severe</option>
                      <option value="hormonal">Hormonal</option>
                      <option value="inflammatory">Inflammatory</option>
                      <option value="post-acne">Post-Acne Marks</option>
                    </Select>
                  </FormGroup>
                </SectionGroup>

                <ButtonGroup>
                  <Button variant="outline" onClick={handleCancel}>
                    <X size={16} />
                    Cancel
                  </Button>
                  <Button type="submit">
                    <Save size={16} />
                    Save Changes
                  </Button>
                </ButtonGroup>
              </Form>
            ) : (
              <div style={{ display: 'grid', gap: '1.5rem' }}>
                <SectionGroup>
                  <SectionSubtitle>Personal Info</SectionSubtitle>
                  <InfoCard>
                    <User size={20} />
                    <InfoContent>
                      <InfoLabel>Full Name</InfoLabel>
                      <InfoValue>{userProfile.name}</InfoValue>
                    </InfoContent>
                  </InfoCard>

                  <InfoCard>
                    <Mail size={20} />
                    <InfoContent>
                      <InfoLabel>Email</InfoLabel>
                      <InfoValue>{userProfile.email}</InfoValue>
                    </InfoContent>
                  </InfoCard>

                  {userProfile.dietaryPreference && (
                    <InfoCard>
                      <Utensils size={20} />
                      <InfoContent>
                        <InfoLabel>Dietary Preference</InfoLabel>
                        <InfoValue>
                          {userProfile.dietaryPreference === 'vegetarian' 
                            ? 'Vegetarian' 
                            : userProfile.dietaryPreference === 'non-vegetarian'
                            ? 'Non-vegetarian'
                            : userProfile.dietaryPreference}
                        </InfoValue>
                      </InfoContent>
                    </InfoCard>
                  )}
                </SectionGroup>

                <SectionDivider />

                <SectionGroup>
                  <SectionSubtitle>Skin Info</SectionSubtitle>
                  <InfoCard>
                    <Target size={20} />
                    <InfoContent>
                      <InfoLabel>Skin Type</InfoLabel>
                      <InfoValue>
                        {realTimeStats.detectedSkinType || userProfile.skinType || 'Not set'}
                      </InfoValue>
                    </InfoContent>
                  </InfoCard>

                  <InfoCard>
                    <TrendingUp size={20} />
                    <InfoContent>
                      <InfoLabel>Acne Type</InfoLabel>
                      <InfoValue>
                        {realTimeStats.detectedAcneType || userProfile.acneType || 'Not set'}
                      </InfoValue>
                    </InfoContent>
                  </InfoCard>
                </SectionGroup>

                <SectionDivider />

                <SectionGroup>
                  <SectionSubtitle>Membership</SectionSubtitle>
                  <InfoCard>
                    <Calendar size={20} />
                    <InfoContent>
                      <InfoLabel>Member Since</InfoLabel>
                      <InfoValue>{new Date(userProfile.createdAt).toLocaleDateString()}</InfoValue>
                    </InfoContent>
                  </InfoCard>
                </SectionGroup>

                <Button 
                  variant="secondary" 
                  onClick={() => navigate('/results')}
                  fullWidth
                >
                  My Results
                </Button>

                {/* Cancel Subscription Button - Available for all plans */}
                <CancelButton
                  fullWidth
                  onClick={handleCancelSubscription}
                  disabled={isCancelling}
                >
                  <X size={16} style={{ marginRight: '0.5rem' }} />
                  {isCancelling ? 'Cancelling...' : 'Cancel Subscription'}
                </CancelButton>

                <Button 
                  variant="outline" 
                  onClick={handleSignOut}
                  fullWidth
                >
                  <LogOut size={16} />
                  Sign Out
                </Button>
              </div>
            )}
          </ProfileCard>

        </ProfileGrid>
      </Container>
    </PageContainer>
  );
};

export default ProfilePage;